import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { useActivePolling } from './useActivePolling';

/**
 * Unified User Activity hook — single source of truth for Dashboard,
 * Analytics, and Intelligence pages. Replaces the old bot-centric
 * useAnalytics/useBotStatus/useTradeHistory data flow.
 *
 * Returns:
 *   summary       - full aggregate (counts, today/7d/30d, by-exchange, by-symbol,
 *                   timeSeries, behavior, recentTrades)
 *   loading       - true on first load
 *   error         - error message string or null
 *   refresh()     - force a re-fetch
 *
 * Auto-refresh: every 30s while the tab is active.
 */
export function useUserActivity({ recent = 20, days = 30, window = null, pollMs = 30000 } = {}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const data = await api.getUserActivitySummary({ recent, days, window });
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [recent, days, window]);

  const interval = useActivePolling(pollMs, true);

  useEffect(() => {
    fetch();
    if (!interval) return;
    const id = setInterval(fetch, interval);
    return () => clearInterval(id);
  }, [fetch, interval]);

  return { summary, loading, error, refresh: fetch };
}

/**
 * Cost-Basis P&L — single source of truth for realized P&L, win rate,
 * profit factor, expectancy, open positions w/ cost basis, daily P&L series.
 *
 * Method options: 'FIFO' (default), 'LIFO', 'HIFO'.
 */
export function useCostBasis({ method = 'FIFO', pollMs = 60000 } = {}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const data = await api.getCostBasisSummary({ method });
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load cost basis');
    } finally {
      setLoading(false);
    }
  }, [method]);

  const interval = useActivePolling(pollMs, true);

  useEffect(() => {
    fetch();
    if (!interval) return;
    const id = setInterval(fetch, interval);
    return () => clearInterval(id);
  }, [fetch, interval]);

  return { summary, loading, error, refresh: fetch };
}

/**
 * Batched dashboard snapshot — 1 call replaces 4 separate polls.
 * Polls every 5s while user is active; pauses when tab hidden or user idle.
 */
export function useDashboardSnapshot() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const data = await api.getDashboardSnapshot();
      setSnapshot(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Dashboard: 5s polling, paused when idle/hidden
  const interval = useActivePolling(5000, true);

  useEffect(() => {
    fetchSnapshot();
    if (!interval) return;
    const id = setInterval(fetchSnapshot, interval);
    return () => clearInterval(id);
  }, [fetchSnapshot, interval]);

  return { snapshot, loading, error, refresh: fetchSnapshot };
}

/**
 * Hook for fetching bot list
 */
export function useBots() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listBots();
      setBots(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  return { bots, loading, error, refresh: fetchBots };
}

/**
 * Hook for fetching bot status
 */
export function useBotStatus(botId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBotStatus(botId);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  // Bots: 10s polling, paused when tab hidden / user idle 5+ min, only when botId is set
  const interval = useActivePolling(10000, !!botId);

  useEffect(() => {
    fetchStatus();
    if (!interval) return;
    const id = setInterval(fetchStatus, interval);
    return () => clearInterval(id);
  }, [fetchStatus, interval]);

  return { status, loading, error, refresh: fetchStatus };
}

/**
 * Hook for fetching bot configuration
 */
export function useBotConfig(botId) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(!!botId); // Only loading if we have a botId
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!botId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBot(botId);
      setConfig(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = async (newConfig) => {
    try {
      const updated = await api.createOrUpdateBot(newConfig.botId, newConfig);
      setConfig(updated);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { config, loading, error, refresh: fetchConfig, updateConfig };
}

/**
 * Hook for fetching analytics data
 */
export function useAnalytics(botId, days = 30) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAnalytics(botId, days);
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, loading, error, refresh: fetchAnalytics };
}

/**
 * Hook for fetching trade history
 */
export function useTradeHistory(botId, days = 7, limit = 100) {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrades = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTradeHistory(botId, days, limit);
      setTrades(data.trades || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId, days, limit]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  return { trades, total, loading, error, refresh: fetchTrades };
}

/**
 * Hook for market trend data (Cap 1)
 */
export function useBotTrend(botId, autoRefresh = false) {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrend = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBotTrend(botId);
      setTrend(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  // Trend: 15s polling, paused when idle/hidden, gated on autoRefresh and botId
  const interval = useActivePolling(15000, autoRefresh && !!botId);

  useEffect(() => {
    fetchTrend();
    if (!interval) return;
    const id = setInterval(fetchTrend, interval);
    return () => clearInterval(id);
  }, [fetchTrend, interval]);

  return { trend, loading, error, refresh: fetchTrend };
}

/**
 * Hook for AI signals (Cap 3)
 */
export function useBotSignal(botId) {
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSignal = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBotSignal(botId);
      setSignal(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  // Signals: 30s polling, paused when idle/hidden, gated on botId
  const interval = useActivePolling(30000, !!botId);

  useEffect(() => {
    fetchSignal();
    if (!interval) return;
    const id = setInterval(fetchSignal, interval);
    return () => clearInterval(id);
  }, [fetchSignal, interval]);

  return { signal, loading, error, refresh: fetchSignal };
}

/**
 * Hook for forecasting data (Cap 2)
 */
export function useBotForecast(botId) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchForecast = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBotForecast(botId);
      setForecast(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  return { forecast, loading, error, refresh: fetchForecast };
}

/**
 * Hook for alert configs (Cap 6)
 */
export function useAlertConfigs() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAlertConfigs();
      setConfigs(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  return { configs, loading, error, refresh: fetchConfigs };
}

/**
 * Hook for supported chains (Cap 7)
 */
export function useSupportedChains() {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChains = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSupportedChains();
      setChains(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChains();
  }, [fetchChains]);

  return { chains, loading, error, refresh: fetchChains };
}

/**
 * Hook for marketplace strategies (Cap 8)
 */
export function useMarketplaceStrategies(filters = {}) {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filtersKey = JSON.stringify(filters);

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getMarketplaceStrategies(filters);
      setStrategies(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  return { strategies, loading, error, refresh: fetchStrategies };
}

/**
 * Hook for system health. `enabled=false` short-circuits both the initial
 * fetch and the polling effect — useful so non-admin users on the Settings
 * page don't spam /api/health (which is now admin-only and would 403).
 */
export function useSystemHealth(enabled = true) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSystemHealth();
      setHealth(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // System health: 60s polling, paused when idle/hidden
  const interval = useActivePolling(60000, enabled);

  useEffect(() => {
    if (!enabled) return;
    fetchHealth();
    if (!interval) return;
    const id = setInterval(fetchHealth, interval);
    return () => clearInterval(id);
  }, [fetchHealth, interval, enabled]);

  return { health, loading, error, refresh: fetchHealth };
}
